import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Helmet } from "react-helmet-async";
import { Shield, UserPlus, Users, MessageSquare, Heart, Flame, Star, Pencil, Check, ArrowRight, Trophy, Eye, EyeOff } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";

const BADGE_ICON = { shield: Shield, "user-plus": UserPlus, users: Users, message: MessageSquare, heart: Heart, flame: Flame };

function Avatar({ src, name, size = "lg" }) {
  const cls = size === "lg" ? "w-24 h-24 text-3xl" : "w-10 h-10 text-sm";
  if (src) return <img src={src} alt={name} className={`${cls} rounded-full object-cover border-2 border-gold/40`} />;
  return (
    <div className={`${cls} rounded-full bg-gold/15 border-2 border-gold/40 flex items-center justify-center text-gold font-bold`}>
      {(name || "?")[0].toUpperCase()}
    </div>
  );
}

function CaseChip({ c }) {
  return (
    <Link to={`/cases/${c.slug}`} className="group flex items-center gap-3 rounded-md border border-white/10 card-surface p-3 hover:border-gold/40 transition-colors" data-testid={`profile-case-${c.slug}`}>
      {c.thumbnail ? <img src={c.thumbnail} alt="" className="w-12 h-12 rounded-sm object-cover shrink-0" /> : <div className="w-12 h-12 rounded-sm bg-white/5 shrink-0" />}
      <span className="text-sm text-white/80 group-hover:text-gold transition-colors line-clamp-2">{c.title}</span>
    </Link>
  );
}

function ChangePassword() {
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!cur || !nw) { toast.error("Enter your current and new password"); return; }
    setBusy(true);
    try {
      await api.put("/me/password", { current_password: cur, new_password: nw });
      toast.success("Password updated");
      setCur(""); setNw("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <div className="mt-6 pt-6 border-t border-white/10" data-testid="change-password">
      <label className="label-mono text-white/40 block mb-2">Change password</label>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="relative">
          <input type={show ? "text" : "password"} value={cur} onChange={(e) => setCur(e.target.value)} autoComplete="current-password"
            placeholder="Current password" className="w-full bg-charcoal border border-white/15 rounded-sm pl-3 pr-10 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none" data-testid="current-password-input" />
        </div>
        <div className="relative">
          <input type={show ? "text" : "password"} value={nw} onChange={(e) => setNw(e.target.value)} autoComplete="new-password"
            placeholder="New password (8+ chars, letter & number)" className="w-full bg-charcoal border border-white/15 rounded-sm pl-3 pr-10 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none" data-testid="new-password-input" />
          <button type="button" onClick={() => setShow((s) => !s)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-white/40 hover:text-gold transition-colors" data-testid="toggle-password-visibility" aria-label={show ? "Hide passwords" : "Show passwords"}>
            {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        </div>
      </div>
      <button onClick={submit} disabled={busy} className="mt-3 inline-flex items-center gap-2 px-4 py-2 border border-white/15 rounded-sm text-sm text-white/80 hover:border-gold/50 transition-colors disabled:opacity-60" data-testid="update-password-btn">
        {busy ? "Updating…" : "Update password"}
      </button>
    </div>
  );
}

export default function ProfilePage() {
  const { userId } = useParams();
  const { user, refreshUser } = useAuth();
  const isOwn = user && user.user_id === userId;
  const [p, setP] = useState(null);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [bio, setBio] = useState("");
  const [choices, setChoices] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    const url = isOwn ? "/me/profile" : `/users/${userId}/profile`;
    api.get(url).then(({ data }) => {
      setP(data);
      setName(data.name || "");
      setBio(data.bio || "");
      setChoices(data.follow_choices || []);
    }).catch(() => setP(false));
  }, [userId, isOwn]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => { if (isOwn && user?.email) setEmail(user.email); }, [isOwn, user]);

  const toggleCase = (cid) => setChoices((cs) => cs.map((c) => c.case_id === cid ? { ...c, highlighted: !c.highlighted } : c));
  const save = async () => {
    setSaving(true);
    try {
      const highlighted_cases = choices.filter((c) => c.highlighted).map((c) => c.case_id);
      const newEmail = email.trim().toLowerCase();
      if (isOwn && newEmail && newEmail !== (user?.email || "").toLowerCase()) {
        await api.put("/me/email", { email: newEmail });
      }
      const { data } = await api.put("/me/profile", { name: name.trim(), bio, highlighted_cases });
      setP(data); setChoices(data.follow_choices || []); setEditing(false);
      await refreshUser?.();
      toast.success("Profile updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const uploadAvatar = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      await api.post("/me/avatar", fd, { headers: { "Content-Type": "multipart/form-data" } });
      load(); toast.success("Photo updated");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); }
  };

  if (p === false) return (
    <div className="min-h-screen bg-charcoal"><Navbar /><div className="max-w-3xl mx-auto px-6 pt-32 text-center text-white/50">Profile not found.</div></div>
  );
  if (!p) return (
    <div className="min-h-screen bg-charcoal"><Navbar /><div className="max-w-3xl mx-auto px-6 pt-32 text-white/40 label-mono">Loading…</div></div>
  );

  const memberSince = p.member_since ? new Date(p.member_since).toLocaleDateString("en-US", { month: "long", year: "numeric" }) : null;

  return (
    <div className="min-h-screen bg-charcoal">
      <Helmet><title>{`${p.name} · Help Solve The Case`}</title></Helmet>
      <Navbar />
      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-28 pb-24">
        <div className="card-surface rounded-lg p-8 border border-white/10" data-testid="profile-card">
          <div className="flex flex-col sm:flex-row sm:items-center gap-6">
            <div className="relative shrink-0">
              <Avatar src={p.picture} name={p.name} />
              {isOwn && editing && (
                <label className="absolute -bottom-1 -right-1 bg-gold text-charcoal rounded-full p-1.5 cursor-pointer" data-testid="avatar-upload-label">
                  <Pencil className="w-3.5 h-3.5" />
                  <input type="file" accept="image/*" className="hidden" disabled={uploading} onChange={(e) => uploadAvatar(e.target.files?.[0])} data-testid="avatar-input" />
                </label>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <h1 className="font-display text-2xl font-bold text-white">{p.name}</h1>
                <span className={`label-mono px-2 py-0.5 rounded-sm border ${p.role === "admin" ? "border-gold/40 text-gold" : "border-white/15 text-white/50"}`}>{p.role === "admin" ? "Detective" : "Member"}</span>
              </div>
              <div className="flex items-center gap-4 mt-2 text-sm text-white/50">
                <span className="inline-flex items-center gap-1"><Trophy className="w-4 h-4 text-gold" /> {p.points} pts</span>
                <span>{p.posts} posts</span>
                {memberSince && <span>Since {memberSince}</span>}
              </div>
            </div>
            {isOwn && !editing && (
              <button onClick={() => setEditing(true)} className="shrink-0 inline-flex items-center gap-2 px-4 py-2 border border-white/15 rounded-sm text-sm text-white/80 hover:border-gold/50 transition-colors" data-testid="edit-profile-btn">
                <Pencil className="w-4 h-4" /> Edit profile
              </button>
            )}
          </div>

          {/* Bio & account */}
          <div className="mt-6">
            {editing ? (
              <div className="space-y-3">
                {isOwn && (
                  <div className="grid sm:grid-cols-2 gap-3">
                    <div>
                      <label className="label-mono text-white/40 block mb-1">Display name</label>
                      <input value={name} onChange={(e) => setName(e.target.value)} maxLength={80}
                        placeholder="Your name" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none" data-testid="name-input" />
                    </div>
                    <div>
                      <label className="label-mono text-white/40 block mb-1">Email</label>
                      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@email.com" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none" data-testid="email-input" />
                    </div>
                  </div>
                )}
                <div>
                  {isOwn && <label className="label-mono text-white/40 block mb-1">Bio</label>}
                  <textarea value={bio} onChange={(e) => setBio(e.target.value)} maxLength={500} rows={3}
                    placeholder="Add a short bio…" className="w-full bg-charcoal border border-white/15 rounded-sm px-3 py-2.5 text-sm text-white placeholder:text-white/30 focus:border-gold/50 outline-none" data-testid="bio-input" />
                </div>
              </div>
            ) : (
              p.bio ? <p className="text-sm text-white/70 leading-relaxed whitespace-pre-line">{p.bio}</p> : <p className="text-sm text-white/30 italic">No bio yet.</p>
            )}
          </div>

          {/* Change password */}
          {editing && isOwn && <ChangePassword />}

          {/* Badges */}
          {p.badges?.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {p.badges.map((b) => {
                const Icon = BADGE_ICON[b.icon] || Star;
                return <span key={b.id} className="inline-flex items-center gap-1.5 label-mono bg-gold/10 border border-gold/25 text-gold/90 px-2.5 py-1 rounded-sm"><Icon className="w-3.5 h-3.5" /> {b.label}</span>;
              })}
            </div>
          )}
        </div>

        {/* Highlighted / chooser */}
        <div className="mt-8">
          <div className="flex items-center gap-2 mb-4"><Star className="w-4 h-4 text-gold" /><h2 className="label-mono">{editing ? "Choose cases to highlight" : "Cases they follow"}</h2></div>
          {editing ? (
            choices.length === 0 ? (
              <p className="text-sm text-white/40">You&apos;re not following any cases yet. <Link to="/cases" className="text-gold">Find a case →</Link></p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3" data-testid="highlight-chooser">
                {choices.map((c) => (
                  <button key={c.case_id} onClick={() => toggleCase(c.case_id)} data-testid={`choose-case-${c.slug}`}
                    className={`flex items-center gap-3 rounded-md border p-3 text-left transition-colors ${c.highlighted ? "border-gold/60 bg-gold/[0.08]" : "border-white/10 card-surface hover:border-white/25"}`}>
                    <span className={`shrink-0 w-5 h-5 rounded-sm border flex items-center justify-center ${c.highlighted ? "bg-gold border-gold" : "border-white/25"}`}>{c.highlighted && <Check className="w-3.5 h-3.5 text-charcoal" />}</span>
                    {c.thumbnail ? <img src={c.thumbnail} alt="" className="w-10 h-10 rounded-sm object-cover" /> : <div className="w-10 h-10 rounded-sm bg-white/5" />}
                    <span className="text-sm text-white/80 line-clamp-2">{c.title}</span>
                  </button>
                ))}
              </div>
            )
          ) : (
            p.highlighted_cases?.length === 0 ? (
              <p className="text-sm text-white/30 italic">No highlighted cases.</p>
            ) : (
              <div className="grid sm:grid-cols-2 gap-3">{p.highlighted_cases.map((c) => <CaseChip key={c.case_id} c={c} />)}</div>
            )
          )}
        </div>

        {editing && (
          <div className="mt-8 flex gap-3">
            <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 px-6 py-3 bg-gold text-charcoal font-semibold rounded-sm hover:bg-gold-hover transition-colors disabled:opacity-60" data-testid="save-profile-btn">
              {saving ? "Saving…" : "Save profile"}
            </button>
            <button onClick={() => { setEditing(false); setName(p.name || ""); setEmail(user?.email || ""); load(); }} className="px-6 py-3 border border-white/15 text-white/70 rounded-sm hover:bg-white/5 transition-colors" data-testid="cancel-profile-btn">Cancel</button>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
